import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Basic ${localStorage.getItem("admin_auth")}` });

const MenuRow = ({ item, onPatch }) => {
  const [price, setPrice] = useState(item.price ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setPrice(item.price ?? ""), [item.price]);

  const dirty = item.price != null && price !== "" && Number(price) !== item.price;
  const hidden = item.available === false;

  const savePrice = async () => {
    const value = Number(price);
    if (price === "" || Number.isNaN(value) || value < 0) {
      toast.error("Введите корректную цену");
      return;
    }
    setSaving(true);
    await onPatch(item.id, { price: value });
    setSaving(false);
  };

  return (
    <div
      className={`grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,1fr)_150px_110px_90px_110px] items-center gap-3 py-3 border-b last:border-0 ${hidden ? "opacity-50" : ""}`}
      data-testid={`admin-menu-row-${item.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <img src={item.image} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
        <div className="min-w-0">
          <div className="font-medium truncate" data-testid={`admin-menu-name-${item.id}`}>{item.name_ru}</div>
          <div className="text-xs text-gray-500 truncate">{item.name_lv}</div>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <Input
          type="number"
          step="0.5"
          min="0"
          value={price}
          disabled={item.price == null}
          placeholder={item.price == null ? "—" : ""}
          onChange={(e) => setPrice(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && dirty && savePrice()}
          className="w-24 h-9"
          data-testid={`admin-menu-price-${item.id}`}
        />
        <Button
          size="sm"
          variant={dirty ? "default" : "ghost"}
          disabled={!dirty || saving}
          onClick={savePrice}
          className="h-9 px-2"
          data-testid={`admin-menu-save-${item.id}`}
          aria-label="Сохранить цену"
        >
          <Check className="w-4 h-4" />
        </Button>
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-600">
        <Switch
          checked={item.price == null}
          onCheckedChange={(v) => onPatch(item.id, v ? { price_tba: true } : { price: Number(price) || 0 })}
          data-testid={`admin-menu-tba-${item.id}`}
        />
        Без цены
      </label>

      <label className="flex items-center gap-2 text-xs text-gray-600">
        <Switch
          checked={!!item.is_new}
          onCheckedChange={(v) => onPatch(item.id, { is_new: v })}
          data-testid={`admin-menu-new-${item.id}`}
        />
        Новинка
      </label>

      <label className="flex items-center gap-2 text-xs text-gray-600">
        <Switch
          checked={!hidden}
          onCheckedChange={(v) => onPatch(item.id, { available: v })}
          data-testid={`admin-menu-visible-${item.id}`}
        />
        {hidden ? "Скрыто" : "Показано"}
      </label>
    </div>
  );
};

export const AdminMenuEditor = ({ categories }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/menu`, { headers: authHeader() });
      setItems(res.data);
    } catch {
      toast.error("Не удалось загрузить меню");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    try {
      const res = await axios.patch(`${API}/admin/menu/${id}`, body, { headers: authHeader() });
      setItems((prev) => prev.map((i) => (i.id === id ? res.data : i)));
      toast.success("Сохранено");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось сохранить");
    }
  };

  if (loading) return <Card className="p-6 text-sm text-gray-500">Загрузка меню...</Card>;

  const groups = categories.length ? categories : [...new Set(items.map((i) => i.category))].map((c) => ({ id: c, name_ru: c }));

  return (
    <div className="space-y-6" data-testid="admin-menu-editor">
      <p className="text-sm text-gray-500">
        Цена сохраняется кнопкой ✓ или Enter. «Без цены» — блюдо видно, но заказать нельзя. «Скрыто» — блюдо не показывается на сайте.
      </p>
      {groups.map((cat) => {
        const rows = items.filter((i) => i.category === cat.id);
        if (!rows.length) return null;
        return (
          <Card key={cat.id} className="p-4" data-testid={`admin-menu-category-${cat.id}`}>
            <h3 className="font-semibold mb-2">{cat.name_ru} <span className="text-gray-400 font-normal">({rows.length})</span></h3>
            {rows.map((item) => <MenuRow key={item.id} item={item} onPatch={patch} />)}
          </Card>
        );
      })}
    </div>
  );
};
